import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Shield, ChevronLeft, MessageSquare, Target, Zap, 
  ArrowRight, Mail, Phone, Calendar, Copy, Check,
  Sparkles, ShieldCheck, AlertCircle, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { BUSINESS_NAME } from '@/src/constants';

interface ScriptData {
  opening: string;
  riskExplanation: string;
  findingsSpotlight: string[];
  businessImpact: string;
  transition: string;
  theOffer: string;
  closing: string;
  followUpEmail: string;
}

export default function ConsultationGuide() {
  const { id } = useParams();
  const [script, setScript] = useState<ScriptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const fetchScript = async () => {
      try {
        const res = await fetch('/api/generate-consultation-script', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ auditId: id })
        });
        if (res.ok) {
          const data = await res.json();
          setScript(data);
        }
      } catch (error) {
        console.error("Failed to fetch script:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchScript();
  }, [id]);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040a] pt-32 pb-20 flex items-center justify-center">
        <div className="container mx-auto px-6 max-w-4xl space-y-8">
          <Skeleton className="h-12 w-64 bg-white/5" />
          <Skeleton className="h-64 w-full bg-white/5 rounded-3xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-40 bg-white/5 rounded-2xl" />
            <Skeleton className="h-40 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!script) {
    return (
      <div className="min-h-screen bg-[#02040a] flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="w-16 h-16 text-accent/50 mb-6" />
        <h1 className="text-2xl font-bold mb-2">Guide Unavailable</h1>
        <p className="text-muted-foreground mb-8">We couldn't generate the script for this audit. Please try again.</p>
        <Button asChild>
          <Link to="/admin">Back to Dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#02040a] py-20">
      <div className="container mx-auto px-6 max-w-5xl">
        {/* Navigation */}
        <Link 
          to="/admin" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-accent transition-colors mb-12 group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Command Center</span>
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="space-y-4">
            <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20 px-3 py-1 uppercase tracking-widest text-[10px]">
              <Sparkles className="w-3 h-3 mr-2" /> Conversion Strategy
            </Badge>
            <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight">Consultation <br /> <span className="text-accent underline decoration-accent/20 underline-offset-8">Playbook</span></h1>
            <p className="text-muted-foreground text-lg max-w-2xl font-normal">
              A tailored talk track designed to walk your client through their results and into a long-term partnership.
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 p-6 rounded-2xl flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center font-bold text-background">FA</div>
            <div className="flex flex-col">
              <span className="text-sm font-bold">Fabiyan Anik</span>
              <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Founder, Agrandintl</span>
            </div>
          </div>
        </div>

        {/* Script Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-8">
            {/* 1. Opening */}
            <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12 space-y-6 relative group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-accent" />
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-widest">01. The Opening</h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => copyToClipboard(script.opening, 'opening')}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {copied === 'opening' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-lg leading-relaxed text-foreground/90 italic border-l-2 border-accent/20 pl-8">
                "{script.opening}"
              </p>
            </div>

            {/* 2. Risk Explanation */}
            <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12 space-y-6 relative group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 text-orange-500" />
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-widest">02. Risk Breakdown</h3>
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  Bridge from the score to what it means for their business continuity:
                </p>
                <p className="text-lg leading-relaxed text-foreground font-medium italic border-l-2 border-orange-500/20 pl-8">
                  "{script.riskExplanation}"
                </p>
              </div>
            </div>

            {/* 3. Findings Spotlight */}
            <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12 space-y-6 relative group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-accent" />
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-widest">03. The Critical Findings</h3>
                </div>
              </div>
              <div className="space-y-6">
                <p className="text-muted-foreground">Focus on these specific points from their report:</p>
                <div className="grid gap-4">
                  {script.findingsSpotlight.map((finding, idx) => (
                    <div key={idx} className="flex gap-4 items-start bg-white/5 p-6 rounded-xl border border-white/5">
                      <div className="w-6 h-6 rounded-full bg-accent/10 text-accent flex items-center justify-center text-[10px] font-bold shrink-0 mt-1">
                        {idx + 1}
                      </div>
                      <p className="text-sm leading-relaxed text-foreground/80 font-medium italic italic">"{finding}"</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 4. Business Impact */}
            <div className="bg-accent/[0.02] border border-accent/10 rounded-3xl p-8 md:p-12 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-accent" />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-widest">04. The Business Impact</h3>
              </div>
              <p className="text-lg leading-relaxed text-foreground font-medium italic border-l-2 border-accent/30 pl-8">
                "{script.businessImpact}"
              </p>
            </div>

            {/* 5 & 6. Transition & Offer */}
            <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12 space-y-12">
              <section className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-green-500" />
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-widest">05. The Pivot</h3>
                </div>
                <p className="text-lg leading-relaxed text-foreground font-medium italic border-l-2 border-green-500/20 pl-8">
                  "{script.transition}"
                </p>
              </section>

              <section className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-accent" />
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-widest">06. The Solutions Offering</h3>
                </div>
                <p className="text-lg leading-relaxed text-foreground font-medium italic border-l-2 border-accent/20 pl-8 bg-accent/5 p-6 rounded-r-xl">
                  "{script.theOffer}"
                </p>
              </section>
            </div>

            {/* 7. Closing */}
            <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center border border-white/10">
                  <Check className="w-4 h-4 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-widest">07. The Close</h3>
              </div>
              <p className="text-lg leading-relaxed text-foreground italic border-l-2 border-white/10 pl-8">
                "{script.closing}"
              </p>
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-8">
            {/* Follow-up Email */}
            <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 space-y-6 lg:sticky lg:top-24">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-accent" />
                  <h3 className="text-sm font-bold uppercase tracking-widest">Follow-up Template</h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => copyToClipboard(script.followUpEmail, 'email')}
                >
                  {copied === 'email' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="bg-background/50 border border-white/5 rounded-xl p-6 relative">
                 <p className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap font-mono">
                   {script.followUpEmail}
                 </p>
              </div>
              <Button asChild className="w-full h-12 rounded-xl bg-accent text-background hover:bg-accent/90" onClick={() => copyToClipboard(script.followUpEmail, 'email')}>
                 <span>Copy Email Body</span>
              </Button>
            </div>

            {/* Quick Tips */}
            <div className="p-8 border border-accent/10 bg-accent/5 rounded-3xl space-y-6">
               <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-accent">Founder Tip</h4>
               <ul className="space-y-4">
                  <li className="flex gap-3 text-xs leading-relaxed text-muted-foreground">
                    <Target className="w-4 h-4 shrink-0 text-accent" />
                    <span>Focus on the "So-What" for their specific industry (Retail vs Cloud).</span>
                  </li>
                  <li className="flex gap-3 text-xs leading-relaxed text-muted-foreground">
                    <Zap className="w-4 h-4 shrink-0 text-accent" />
                    <span>Always link a finding to a potential dollar loss or trust loss.</span>
                  </li>
                  <li className="flex gap-3 text-xs leading-relaxed text-muted-foreground">
                    <Phone className="w-4 h-4 shrink-0 text-accent" />
                    <span>Keep the tone helpful and strategic, never aggressive or alarmist.</span>
                  </li>
               </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
